/**
 * Team stats (doubles): shows stats for each player in the team with links to their profiles.
 * Route: /team-stats?ids=id1,id2
 * Available for all plans; ads depend on viewer's plan.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as readApi from '../api/readApi';
import { db } from '../firebase/config';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import { usePlan } from '../contexts/PlanContext';
import { useGame } from '../contexts/GameContext';
import { getUserDisplayName } from '../utils/nameFormat';
import DataFetchOverlay from '../components/DataFetchOverlay';
import CachedAvatar from '../components/CachedAvatar';
import ProfileLink from '../components/ProfileLink';
import GoogleAd from '../components/GoogleAd';
import './TeamStats.css';

function PlayerStatsBlock({ userId, playersMap, statsDoc, enabledGames }) {
  const displayName = playersMap[userId]
    ? getUserDisplayName(playersMap[userId])
    : (playersMap[userId]?.displayName || 'Unknown');
  const avatarUrl = playersMap[userId]?.photoURL || playersMap[userId]?.avatarURL || '';

  const getStatsForGame = (gameId) => {
    if (!statsDoc) return null;
    const gameStats = statsDoc.gameStats || {};
    const gs = gameStats[gameId];
    if (gs) {
      const mw = gs.matchesWon ?? 0;
      const ml = gs.matchesLost ?? 0;
      const total = mw + ml;
      return total > 0 ? { matchesWon: mw, matchesLost: ml, totalPoints: gs.totalPoints ?? 0, totalMatches: total, winRate: ((mw / total) * 100).toFixed(1) } : null;
    }
    return null;
  };

  const gamesToShow = Array.isArray(enabledGames) ? enabledGames : [];
  const rows = gamesToShow
    .map((game) => ({ game, stats: getStatsForGame(game.id) }))
    .filter((r) => r.stats && r.stats.totalMatches > 0);

  return (
    <div className="team-stats-player-block card-ui">
      <ProfileLink userId={userId} className="team-stats-player-identity">
        <CachedAvatar url={avatarUrl} name={displayName} className="team-stats-player-avatar" alt={displayName} />
        <span className="team-stats-player-name">{displayName}</span>
      </ProfileLink>
      {rows.length === 0 ? (
        <p className="team-stats-no-stats">No match stats yet.</p>
      ) : (
        <table className="team-stats-mini-table">
          <thead>
            <tr>
              <th>Game</th>
              <th>W</th>
              <th>L</th>
              <th>Win%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ game, stats }) => (
              <tr key={game.id}>
                <td>{game.icon} {game.name}</td>
                <td>{stats.matchesWon}</td>
                <td>{stats.matchesLost}</td>
                <td>{stats.winRate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function TeamStats() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getPlayers } = usePlayerCache();
  const { showAds } = usePlan();
  const { games: enabledGames } = useGame();
  const idsParam = searchParams.get('ids') || '';
  const playerIds = idsParam.split(',').map((s) => s.trim()).filter(Boolean);

  const [playersMap, setPlayersMap] = useState({});
  const [statsByPlayer, setStatsByPlayer] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (playerIds.length === 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const map = await getPlayers(playerIds);
        if (cancelled) return;
        setPlayersMap(map);
        const stats = {};
        await Promise.all(
          playerIds.map(async (uid) => {
            if (cancelled) return;
            const s = await readApi.getPlayerStats(uid);
            if (!cancelled) stats[uid] = s;
          })
        );
        if (!cancelled) setStatsByPlayer(stats);
      } catch (err) {
        console.error('Error fetching team stats:', err);
        if (!cancelled) setPlayersMap({});
        setStatsByPlayer({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- idsParam drives playerIds; getPlayers identity would cause loop
  }, [idsParam]);

  if (playerIds.length === 0) {
    return (
      <div className="team-stats-page">
        <div className="team-stats-container">
          <button onClick={() => navigate(-1)} className="btn-secondary btn-back">Back</button>
          <p className="team-stats-empty">No team specified. Use ?ids=id1,id2</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <DataFetchOverlay message="Loading team stats…" />;
  }

  return (
    <div className="team-stats-page">
      <div className="team-stats-container">
        <div className="team-stats-header-row">
          <button onClick={() => navigate(-1)} className="btn-secondary btn-back">Back</button>
        </div>
        {showAds && <GoogleAd slotKey="BANNER" className="team-stats-ad" />}
        <h1 className="team-stats-title">Team stats</h1>
        <div className="team-stats-players">
          {playerIds.map((userId) => (
            <PlayerStatsBlock
              key={userId}
              userId={userId}
              playersMap={playersMap}
              statsDoc={statsByPlayer[userId]}
              enabledGames={enabledGames}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
